import moment from 'moment';
import {Types} from 'mongoose';
import store from '@/store';
import {LoginTypes} from '@/enums/LoginTypes.enum';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {RecorderApi} from '@/classes/api/recorder.api.class';
import {getLogAuthorName, getLogTypeMeta, htmlToText} from '@/components/timeline/logEntryMeta';
import {canReadType} from '@/components/app/search/searchAccess';
import {PaletteHit, routeOfEntity} from '@/components/app/search/searchEntities';
import {QuickFact, QuickLog} from '@/components/app/search/searchQuickViewData';
import {LogEntryTypes} from '@/enums/LogEntryEnums';
import {earlyWarningCourseLine, EarlyWarningLogPayload} from '@/components/timeline/earlyWarningLog.helper';
import {CoursePresenceState} from '@/enums/CoursePresenceState.enum';
import {CourseLogPayload, courseLogText, genericLogText, isIdOnly, readableText, shorten} from '@/components/app/search/searchLogText';

/** Entries of the one recorder request: pinned ones come first (recorder sorts by `sticky`), then the newest */
const LOG_LIMIT = 8;
const MAX_PINNED = 3;
const MAX_NEWEST = 3;
const MAX_TEXT_LENGTH = 280;

type RawLog = {
    _id: unknown;
    type?: string;
    sortingDate?: Date | string;
    createdAt?: Date | string;
    sticky?: boolean;
    author?: unknown;
    authorType?: string;
    payload?: Record<string, unknown>;
};

export type LogSummary = { lastContact: QuickFact | null; logs: QuickLog[] };

function str(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

/**
 * Plain text of the entry: note / protocol text, mail subject and preview, call summary.
 * Course logs: `payload.subject` is the Fach reference (an ObjectId), not a mail subject – their text is the message
 * or a summary of the payload. A value that is only an ObjectId is never shown.
 */
function textOfLog(log: RawLog): string {
    const payload = log.payload || {};
    if (log.type === LogEntryTypes.EARLY_WARNING) {
        const early = payload as EarlyWarningLogPayload;
        const text = [early.trigger, earlyWarningCourseLine(early)].filter((el) => el && !isIdOnly(el)).join(' – ') || readableText(htmlToText(early.message));
        return shorten(text, MAX_TEXT_LENGTH);
    }
    const message = htmlToText(str(payload.message));
    if (log.type === LogEntryTypes.COURSE) {
        const state = CoursePresenceState.Record[payload.state as CoursePresenceState];
        return shorten(courseLogText(payload as CourseLogPayload, message, state ? state.text : ''), MAX_TEXT_LENGTH);
    }
    const preview = htmlToText(str(payload.messagePreview) || str(payload.summary));
    const subject = htmlToText(str(payload.subject));
    return shorten(genericLogText(message, subject, preview), MAX_TEXT_LENGTH);
}

function timeOf(log: RawLog): number {
    const date = log.sortingDate || log.createdAt;
    return date ? moment(date).valueOf() : 0;
}

function toQuickLog(log: RawLog, hit: PaletteHit): QuickLog {
    const meta = getLogTypeMeta(log.type);
    const id = String(log._id);
    const route = routeOfEntity(hit.entity, hit.id);
    const time = timeOf(log);
    return {
        id,
        icon: meta.icon,
        tone: meta.tone,
        typeLabel: meta.label,
        dateText: time ? moment(time).format('DD.MM.YYYY') : '',
        author: log.type === LogEntryTypes.EARLY_WARNING ? 'Frühwarnung' : getLogAuthorName(log as never),
        text: textOfLog(log),
        pinned: log.sticky === true,
        to: typeof route === 'string' ? {path: route, query: {log: id}} : null,
    };
}

function lastContactOf(logs: RawLog[], hit: PaletteHit): QuickFact {
    // an early warning is written by the system and is no contact
    const last = logs.filter((log) => log.type !== LogEntryTypes.EARLY_WARNING).sort((a, b) => timeOf(b) - timeOf(a))[0];
    if (!last) {
        return {label: 'Letzter Kontakt', value: 'noch kein Eintrag'};
    }
    const time = timeOf(last);
    const parts = [getLogTypeMeta(last.type).label, time ? moment(time).locale('de').fromNow() : '', getLogAuthorName(last as never)];
    const quick = toQuickLog(last, hit);
    return {label: 'Letzter Kontakt', value: parts.filter((el) => el).join(' · '), detail: quick.text || undefined, to: quick.to || undefined};
}

/** Fields of the request, each only when readable; author names only with read access to employee names */
function requestOf(id: Types.ObjectId) {
    const optional = ['createdAt', 'sticky', 'payload'].filter((field) => canReadType(CrudEntityTypes.LOG_ENTRY, field));
    const withAuthor = canReadType(CrudEntityTypes.LOG_ENTRY, 'author', 'authorType');
    const withNames = withAuthor && canReadType(CrudEntityTypes.EMPLOYEE, 'firstName', 'lastName');
    return {
        filter: {object: [id]},
        page: 1,
        limit: LOG_LIMIT,
        sortBy: 'sortingDate',
        sortDesc: true,
        fields: ['type', 'sortingDate', ...optional, ...(withAuthor ? ['author', 'authorType'] : [])],
        ...(withNames ? {populate: {author: {fields: ['firstName', 'lastName']}}} : {}),
    };
}

/**
 * "Letzter Kontakt" and the compact entry list of the quick view with one silent recorder request.
 * Null without read access to log entries (and always for partner logins).
 */
export async function loadLogSummary(id: Types.ObjectId, hit: PaletteHit): Promise<LogSummary | null> {
    if (store.state.userType === LoginTypes.PARTNER || !canReadType(CrudEntityTypes.LOG_ENTRY, 'object', 'type', 'sortingDate')) {
        return null;
    }
    const resp = await RecorderApi.find(requestOf(id) as never, {handledStatuses: [403, 404]});
    const raw = (resp?.logEntries || []) as unknown as RawLog[];
    const pinned = raw.filter((log) => log.sticky === true).slice(0, MAX_PINNED);
    const newest = raw.filter((log) => log.sticky !== true).sort((a, b) => timeOf(b) - timeOf(a)).slice(0, MAX_NEWEST);
    return {
        lastContact: lastContactOf(raw, hit),
        logs: [...pinned, ...newest].map((log) => toQuickLog(log, hit)),
    };
}
